import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { fmtTs } from './globalTz';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * The number to show an analyst as "Incident #". Sentinel's own `incidentNumber`
 * is a per-workspace counter; for incidents unified with Microsoft Defender XDR,
 * `graphIncidentId` (ARM's `providerIncidentId` / Graph's incident id) is the
 * number Sentinel and Defender XDR actually display to the user. Prefer that so
 * the number shown in Kloudsecure matches what "Investigate in Microsoft
 * Defender XDR" / the Sentinel portal show for the same incident.
 */
export function sentinelIncidentNumber(incident: { incidentNumber?: string | number }): string {
  if (incident.incidentNumber === undefined || incident.incidentNumber === null || incident.incidentNumber === '') return '';
  return String(incident.incidentNumber);
}

export function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return fmtTs(date, { month: 'short', day: 'numeric' });
}

export function formatDate(dateStr: string): string {
  return fmtTs(dateStr, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

export function calcMTTD(incidents: Array<{ createdTime: string; lastUpdateTime: string; status: string; assignedTo?: string }>): string {
  const active = incidents.filter(i => {
    const s = i.status?.toLowerCase();
    return s !== 'closed' && s !== 'redirected';
  });
  if (!active.length) return 'N/A';
  const now = Date.now();
  const hours = active.map(i => {
    const created = new Date(i.createdTime).getTime();
    const updated = new Date(i.lastUpdateTime).getTime();
    return (updated > created ? updated - created : now - created) / 3600000;
  });
  const avg = hours.reduce((a, b) => a + b, 0) / hours.length;
  if (avg < 1) return `${Math.round(avg * 60)}m`;
  if (avg < 24) return `${avg.toFixed(1)}h`;
  return `${(avg / 24).toFixed(1)}d`;
}

export function calcMTTR(incidents: Array<{ createdTime: string; lastUpdateTime: string; status: string }>): string {
  const resolved = incidents.filter(i => i.status?.toLowerCase() === 'closed');
  if (!resolved.length) return 'N/A';
  const hours = resolved.map(i => (new Date(i.lastUpdateTime).getTime() - new Date(i.createdTime).getTime()) / 3600000);
  const avg = hours.reduce((a, b) => a + b, 0) / hours.length;
  if (avg < 1) return `${Math.round(avg * 60)}m`;
  if (avg < 24) return `${avg.toFixed(1)}h`;
  return `${(avg / 24).toFixed(1)}d`;
}

export function calcNoiseRatio(incidents: Array<{ classification?: string }>): string {
  const total = incidents.length;
  if (!total) return 'N/A';
  const noise = incidents.filter(i => i.classification && ['FalsePositive', 'BenignPositive', 'Undetermined'].includes(i.classification)).length;
  return `${Math.round((noise / total) * 100)}%`;
}
